<script setup lang="ts">
import { nextTick, Ref, ref, watchEffect } from 'vue';
import { SearchFilter, FilterOption } from '../../../search-panel-types';
import FilterFieldLayout from './FilterFieldLayout.vue';
import FilterChip from './FilterChip.vue';
import useLocale from '../../../composables/useLocale';
import SearchByFilterOptions from './SearchByFilterOptions.vue';

type Props = {
  fieldKey: string;
  items: FilterOption[];
  label: string;
  selected: SearchFilter;
};

const { t } = useLocale();
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'filters-updated', filter: SearchFilter): void;
}>();
const selectedInternal: Ref<FilterOption[]> = ref([]);
const defaultShowedOptions = 4;
const showedOptions = ref(defaultShowedOptions);
const chipRefs: Ref<any[]> = ref([]);
const showAllButtonRef = ref<HTMLElement | null>(null);

watchEffect(() => {
  if (!props.selected[props.fieldKey]) {
    selectedInternal.value = [];
    return;
  }
  selectedInternal.value = props.selected[props.fieldKey];
});

const isSelected = (value: string): boolean => !!selectedInternal.value.find((el) => el.value === value);

const toggleOption = (item: FilterOption) => {
  if (isSelected(item.value)) {
    selectedInternal.value = selectedInternal.value.filter((selectedChip) => selectedChip.value !== item.value);
  } else {
    selectedInternal.value = [...selectedInternal.value, item];
  }
  emit('filters-updated', { [props.fieldKey]: selectedInternal.value });
};

const isShowedAllOptions = (): boolean => showedOptions.value === props.items.length;

const toggleShowedFilters = async () => {
  const wasShowingAll = isShowedAllOptions();
  showedOptions.value = wasShowingAll ? defaultShowedOptions : props.items.length;

  await nextTick();

  if (!wasShowingAll) {
    const button = chipRefs.value[0]?.$el?.querySelector('button');
    if (button) {
      button.focus();
    }
  } else {
    if (showAllButtonRef.value) {
      const button = (showAllButtonRef.value as any)?.$el;
      if (button) {
        button.focus();
      }
    }
  }
};

const update = (value: string) => {
  if (!value) {
    return;
  }
  const filter = props.items.find((item) => item.value === value);

  if (!filter || isSelected(filter.value)) {
    return;
  }
  toggleOption(filter);
};
</script>

<template>
  <FilterFieldLayout v-show="props.items?.length > 0" :label="props.label ?? ''">
    <template v-if="props.items?.length > 0" #content>
      <div role="group" :aria-label="t(props.label)">
        <SearchByFilterOptions
          v-if="props.items.length > defaultShowedOptions"
          @update="update"
          :items="props.items"
          :id="props.label"
          class="pt-2 pb-0"
        />
        <div class="d-flex flex-wrap gap-between-chips pa-2 pt-4">
          <div v-for="(item, index) in props.items?.slice(0, showedOptions)" :key="item.label">
            <FilterChip
              v-if="item.count > 0"
              class="mb-0"
              :key="item.value"
              :ref="
                (el) => {
                  if (el) chipRefs[index] = el;
                }
              "
              :is-selected="isSelected(item.value)"
              :label="item.label"
              :count="item.count"
              :aria-pressed="isSelected(item.value)"
              @click="toggleOption(item)"
            />
          </div>
        </div>
        <div v-if="props.items?.length > defaultShowedOptions" class="pl-2">
          <div v-if="!isShowedAllOptions()" class="btn-container d-flex align-center rounded mb-2">
            <v-btn
              ref="showAllButtonRef"
              :id="`show_all_${fieldKey}`"
              variant="text"
              color="primary"
              class="text-none rounded"
              @click="toggleShowedFilters"
              :ripple="false"
            >
              {{ t('button_show_all') }}
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
          <div v-else class="btn-container d-flex align-center rounded mb-2">
            <v-btn
              :id="`show_less_${fieldKey}`"
              variant="text"
              color="primary"
              class="text-none rounded"
              @click="toggleShowedFilters"
              :ripple="false"
            >
              {{ t('label_readmore_less') }}
              <v-icon>mdi-minus</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </template>
  </FilterFieldLayout>
</template>

<style scoped lang="scss">
.v-list {
  padding: 0;
  max-height: 400px;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 3.5px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3.5px;
    background-color: #a2a2a2;
    height: 110px;
  }
}
.v-list-item__content > * {
  flex: auto;
}
.v-list-item__content {
  justify-content: space-between;
}
.v-list-item {
  border-bottom: 1px solid #b9b9b9;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    .v-list-item__title {
      color: var(--v-primary-base);
    }
  }
}
.v-list-item--link:before {
  background-color: var(--v-primary-base);
}

.gap-between-chips {
  gap: 14px;
}

.btn-container {
  height: 36px !important;
  width: fit-content;
  margin-top: 6px;
}

.btn-container:hover {
  box-shadow: inset 0 0 0 36px rgba(0, 0, 0, 0.04); /* simulate hover area */
}

.btn-container:active {
  box-shadow: inset 0 0 0 36px rgba(0, 0, 0, 0.2); /* simulate pressed area */
}

.v-btn:hover {
  --v-hover-opacity: 0;
}
</style>
